import { m } from '@pricedrop/i18n';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Plus from 'lucide-react/dist/esm/icons/plus';
import { Button } from '@/components/ui/button';
import { useAuthModal } from '@/lib/auth-modal-context';
import StarRating from './star-rating';

interface Review {
  id: string;
  rating: number;
  content: string;
  userId: string;
  userName?: string;
  createdAt: string;
  updatedAt: string;
}

interface ReviewHeaderProps {
  reviews: Review[];
  isAuthenticated: boolean;
  hasUserReviewed: boolean;
  hasDraft: boolean;
  onAddReview: () => void;
}

function getAverageRating(reviews: Review[]): number {
  if (reviews.length === 0) return 0;
  const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
  return Math.round((sum / reviews.length) * 10) / 10;
}

export default function ReviewHeader({
  reviews,
  isAuthenticated,
  hasUserReviewed,
  hasDraft,
  onAddReview,
}: ReviewHeaderProps) {
  const averageRating = getAverageRating(reviews);
  const { requireAuth } = useAuthModal();

  function handleAddReview() {
    requireAuth(onAddReview, {
      title: m.auth_requireAuth_reviews_title(),
      description: m.auth_requireAuth_reviews_description(),
    });
  }

  return (
    <div className="flex flex-row-reverse items-center justify-between">
      <div className="flex flex-row-reverse items-center gap-3">
        <MessageSquare className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          ביקורות ({reviews.length})
        </h2>
        {reviews.length > 0 && (
          <div className="flex flex-row-reverse items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">({averageRating})</span>
            <StarRating rating={Math.round(averageRating)} size="sm" />
          </div>
        )}
      </div>

      {!hasUserReviewed && (
        <Button
          onClick={isAuthenticated ? onAddReview : handleAddReview}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        >
          <Plus className="me-2 h-4 w-4" />
          {hasDraft ? 'המשך כתיבת ביקורת' : 'הוסף ביקורת'}
        </Button>
      )}
    </div>
  );
}
